import {
  Alert as MuiAlert,
  Autocomplete,
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";

import { Box } from "@mui/system";
import React, { Fragment, forwardRef, useState } from "react";
import HttpService from "../services/HttpService";
import CircularProgress from "@mui/material/CircularProgress";
import uploadImg from "./../images/upload.svg";
import FileUpload from "react-mui-fileuploader";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Labels from "../labels/Labels";

export default function CampaignUpload(props) {
  const queryClient = useQueryClient();

  // Get campaign type from cookie or default to 0 (file)

  const [campaignType, setCampaignType] = useState(0);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [fieldType, setFieldType] = useState("all");
  const [employeeAttribute, setEmployeeAttribute] = useState("");
  const [startDate, setStartDate] = useState("");

  // Save campaign type to cookie when it changes
  const handleCampaignTypeChange = (event, newType) => {
    setCampaignType(newType);
    // Set cookie to expire in 30 days
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 30);
    const typeValue = newType === 1 ? "report" : "file";
    document.cookie = `campaignType=${typeValue}; expires=${expiryDate.toUTCString()}; path=/`;
  };

  // Fetch employers
  const fetchEmployers = async () => {
    const { data } = await HttpService.getAxiosClient().get(
      window.API_URL + `/employer/all?excludeRelations=true`
    );
    return data;
  };

  const { data: employers = [], isLoading: isLoadingEmployers } = useQuery({
    queryKey: ["employers"],
    queryFn: fetchEmployers,
  });

  // Fetch notification templates
  const fetchNotificationTemplates = async () => {
    const { data } = await HttpService.getAxiosClient().get(
      window.API_URL + `/campaigns/notification-templates`
    );
    return data;
  };

  const { data: templates = [], isLoading: isLoadingTemplates } = useQuery({
    queryKey: ["notificationTemplates"],
    queryFn: fetchNotificationTemplates,
  });

  // Fetch report configs (only when campaign type is 'report')
  const fetchReportConfigs = async () => {
    const { data } = await HttpService.getAxiosClient().get(
      window.API_URL + `/report/config`
    );
    return data;
  };

  const { data: reportConfigs = [], isLoading: isLoadingReports } = useQuery({
    queryKey: ["reportConfigs"],
    queryFn: fetchReportConfigs,
    enabled: campaignType === 1, // Only fetch when campaign type is report (1)
  });

  const uploadFileMutation = useMutation(
    (formData) => {
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      return HttpService.getAxiosClient().post(
        window.API_URL + `/campaigns/upload`,
        formData,
        config
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["paymentsSummary"] });
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "campaignUpload.fileUploadedSuccess",
            "File uploaded succesfully"
          ),
          onClose: () => {
            //props.setOpen(false);
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        const msg = err?.response?.data ? err.response.data : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const [filesToUpload, setFilesToUpload] = useState([]);
  const [buttonToUploadEnabled, setButtonToUploadEnabled] = useState(false);
  const handleFilesChange = (files) => {
    setFilesToUpload([...files]);
    if (files.length === 1) {
      setButtonToUploadEnabled(true);
    } else {
      setButtonToUploadEnabled(false);
    }
  };

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  const uploadFiles = () => {
    let formData = new FormData();

    // Add campaign type (convert numeric to string)
    const typeValue = campaignType === 1 ? "report" : "file";
    formData.append("campaignType", typeValue);

    if (campaignType === 0) {
      // File upload flow (campaignType === 0)
      filesToUpload.forEach((file) => formData.append("file", file));

      // Add employer id
      if (selectedEmployer) {
        formData.append("employerId", selectedEmployer.idEmployer);
      }

      // Add field type
      formData.append("fieldType", fieldType);

      // Add optional fields if provided
      if (selectedTemplate) {
        formData.append(
          "templateId",
          selectedTemplate.idNotificationTemplate
        );
      }
      if (employeeAttribute && employeeAttribute.trim() !== "") {
        formData.append("employeeAttribute", employeeAttribute.trim());
      }
      if (startDate && startDate.trim() !== "") {
        formData.append("startDate", startDate.trim());
      }
    } else {
      // Report flow - store report config as additional metadata
      if (selectedReport) {
        const reportMetadata = JSON.stringify({
          idReportConfig: selectedReport.idReportConfig,
          reportName: selectedReport.reportName,
          reportDescription: selectedReport.reportDescription,
        });
        formData.append("additionalMetadata", reportMetadata);
      }

      // Add employer id
      if (selectedEmployer) {
        formData.append("employerId", selectedEmployer.idEmployer);
      }

      // Add optional fields if provided
      if (selectedTemplate) {
        formData.append(
          "templateId",
          selectedTemplate.idTemplate || selectedTemplate.idNotificationTemplate
        );
      }
      if (employeeAttribute && employeeAttribute.trim() !== "") {
        formData.append("employeeAttribute", employeeAttribute.trim());
      }
      if (startDate && startDate.trim() !== "") {
        formData.append("startDate", startDate.trim());
      }
    }

    uploadFileMutation.mutateAsync(formData);
  };

  return (
    <Box component="form" noValidate autoComplete="off">
      <Fragment>
        <Fragment>
          <Grid
            style={{ marginLeft: "20px", marginRight: "10px", marginTop: 0 }}
            container
            spacing={1.5}
          >
            <Container maxWidth="xlg">
              <Grid container spacing={1.5} style={{ maxWidth: "80%" }}>
                <Grid item xs={12} md={4}>
                  <br />
                  <br />
                  <img
                    src={uploadImg}
                    alt="upload"
                    width="50%"
                    loading="lazy"
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Fragment>
                    {/* Mode Selection Tabs */}
                    <Box
                      sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
                    >
                      <Tabs
                        value={campaignType}
                        onChange={handleCampaignTypeChange}
                        aria-label="campaign type"
                        variant="fullWidth"
                        sx={{
                          "& .MuiTab-root": {
                            color: "#0d7f81",
                            fontWeight: 500,
                          },
                          "& .Mui-selected": {
                            color: "#0d7f81 !important",
                          },
                          "& .MuiTabs-indicator": {
                            backgroundColor: "#0d7f81",
                          },
                        }}
                      >
                        <Tab
                          icon={<UploadFileIcon />}
                          label={Labels.getLabel(
                            "campaignUpload.type.file",
                            "File Upload"
                          )}
                          iconPosition="start"
                        />
                        <Tab
                          icon={<AssessmentIcon />}
                          label={Labels.getLabel(
                            "campaignUpload.type.report",
                            "Report Campaign"
                          )}
                          iconPosition="start"
                        />
                      </Tabs>
                    </Box>

                    <Grid
                      container
                      spacing={2}
                      style={{ marginBottom: "20px" }}
                    >
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={employers}
                          getOptionKey={(option) => option.idEmployer}
                          getOptionLabel={(option) =>
                            `${option.idEmployer || ""} - ${option.description || ""}`
                          }
                          value={selectedEmployer}
                          onChange={(event, newValue) => {
                            setSelectedEmployer(newValue);
                          }}
                          loading={isLoadingEmployers}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={Labels.getLabel(
                                "campaignUpload.employer",
                                "Employer"
                              )}
                              variant="outlined"
                              required
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {isLoadingEmployers ? (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={Labels.getLabel(
                            "campaignUpload.startDate",
                            "Start Date"
                          )}
                          type="datetime-local"
                          variant="outlined"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          helperText={Labels.getLabel(
                            "campaignUpload.startDateHelp",
                            "Defaults to now if not specified"
                          )}
                        />
                      </Grid>
                      {campaignType === 1 && (
                        <Grid item xs={12}>
                          <Autocomplete
                            options={reportConfigs}
                            getOptionLabel={(option) =>
                              `${option.idReportConfig || ""} - ${
                                option.reportName || ""
                              }`
                            }
                            value={selectedReport}
                            onChange={(event, newValue) => {
                              setSelectedReport(newValue);
                            }}
                            loading={isLoadingReports}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={Labels.getLabel(
                                  "campaignUpload.reportConfig",
                                  "Report Configuration"
                                )}
                                variant="outlined"
                                required
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {isLoadingReports ? (
                                        <CircularProgress
                                          color="inherit"
                                          size={20}
                                        />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Grid>
                      )}
                      {campaignType === 0 && (
                        <Grid item xs={12}>
                          <FormControl fullWidth required>
                            <InputLabel>
                              {Labels.getLabel(
                                "campaignUpload.fieldType",
                                "File Field Type"
                              )}
                            </InputLabel>
                            <Select
                              value={fieldType}
                              onChange={(e) => setFieldType(e.target.value)}
                              label={Labels.getLabel(
                                "campaignUpload.fieldType",
                                "File Field Type"
                              )}
                            >
                              <MenuItem value="employee_id">
                                {Labels.getLabel(
                                  "campaignUpload.fieldType.employeeId",
                                  "Employee ID only"
                                )}
                              </MenuItem>
                              <MenuItem value="email">
                                {Labels.getLabel(
                                  "campaignUpload.fieldType.email",
                                  "Email only"
                                )}
                              </MenuItem>
                              <MenuItem value="phone">
                                {Labels.getLabel(
                                  "campaignUpload.fieldType.phone",
                                  "Phone only"
                                )}
                              </MenuItem>
                              <MenuItem value="all">
                                {Labels.getLabel(
                                  "campaignUpload.fieldType.all",
                                  "All fields (Employee ID, Employer ID, Email, First Name, Last Name, Full Name, Phone)"
                                )}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Autocomplete
                          options={templates.filter((template) => {
                            // Only filter for file upload mode
                            if (campaignType !== 0) return true;

                            const templateType = (
                              template.typeTemplate || ""
                            ).toUpperCase();

                            // Phone only: show only SMS templates
                            if (fieldType === "phone") {
                              return templateType === "SMS";
                            }

                            // Email only: show only EMAIL templates
                            if (fieldType === "email") {
                              return templateType === "EMAIL";
                            }

                            // Employee ID or All: show all templates
                            return true;
                          })}
                          getOptionKey={(option) => option.idNotificationTemplate}
                          getOptionLabel={(option) =>
                            `${option.notificationTypeId || ""} - ${option.idProcess || ""} - ${
                              option.typeTemplate || ""
                            } (${option.lang || ""})`
                          }
                          value={selectedTemplate}
                          onChange={(event, newValue) => {
                            setSelectedTemplate(newValue);
                          }}
                          loading={isLoadingTemplates}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={Labels.getLabel(
                                "campaignUpload.notificationTemplate",
                                "Notification Template"
                              )}
                              variant="outlined"
                              required
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {isLoadingTemplates ? (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={Labels.getLabel(
                            "campaignUpload.employeeAttribute",
                            "Employee Attribute (Optional)"
                          )}
                          variant="outlined"
                          value={employeeAttribute}
                          onChange={(e) => setEmployeeAttribute(e.target.value)}
                          helperText={Labels.getLabel(
                            "campaignUpload.employeeAttributeHelp",
                            "Additional employee attribute for campaign"
                          )}
                        />
                      </Grid>
                    </Grid>

                    {campaignType === 0 && (
                      <FileUpload
                        multiFile={false}
                        onFilesChange={handleFilesChange}
                        title={Labels.getLabel(
                          "campaignUpload.uploadCsvPrompt",
                          "Please upload the CSV file"
                        )}
                        showPlaceholderImage={false}
                        allowedExtensions={["csv"]}
                        maxUploadFiles={1}
                        header={Labels.getLabel(
                          "campaignUpload.dropHere",
                          "> DRAG AND DROP HERE <"
                        )}
                        BannerProps={{
                          elevation: 0,
                          variant: "outlined",
                          sx: {
                            "& .MuiGrid-item": {
                              color: "#0d7f81",
                              backgroundColor: "aliceblue",
                              borderColor: "#0d7f81",
                            },
                            "& .MuiButtonBase-root": {
                              color: "#0d7f81",
                              backgroundColor: "white",
                              borderColor: "#0d7f81",
                            },
                          },
                        }}
                        ContainerProps={{
                          elevation: 0,
                          variant: "outlined",
                          sx: {
                            p: 1,
                            "& .MuiGrid-item": {
                              color: "#1976d2",
                              backgroundColor: "aliceblue",
                              borderColor: "#1976d2",
                            },
                            "& .MuiGrid-root": {
                              color: "#1976d2",
                              backgroundColor: "aliceblue",
                              borderColor: "#1976d2",
                            },
                          },
                        }}
                        onContextReady={(context) => {}}
                      />
                    )}
                    <Button
                      disabled={
                        campaignType === 0
                          ? !buttonToUploadEnabled ||
                            !selectedTemplate ||
                            !selectedEmployer
                          : !selectedReport ||
                            !selectedTemplate ||
                            !selectedEmployer
                      }
                      style={{ margin: "10px" }}
                      onClick={(e) => uploadFiles(e)}
                      variant="contained"
                    >
                      {uploadFileMutation.isLoading ? (
                        <CircularProgress
                          size="1.5rem"
                          style={{ paddingRight: "1px", color: "white" }}
                        />
                      ) : (
                        <CloudUploadIcon style={{ paddingRight: "5px" }} />
                      )}
                      {campaignType === 0
                        ? Labels.getLabel(
                            "campaignUpload.uploadButton",
                            "Upload"
                          )
                        : Labels.getLabel(
                            "campaignUpload.createButton",
                            "Create Campaign"
                          )}
                    </Button>
                  </Fragment>
                </Grid>
              </Grid>

              <Snackbar
                open={feedback.open}
                autoHideDuration={6000}
                onClose={
                  feedback.onClose ? feedback.onClose : handleFeedbackClose
                }
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <Alert
                  onClose={
                    feedback.onClose ? feedback.onClose : handleFeedbackClose
                  }
                  severity={feedback.type}
                  sx={{ width: "100%" }}
                >
                  {feedback.message}
                </Alert>
              </Snackbar>
            </Container>
          </Grid>
        </Fragment>
      </Fragment>
    </Box>
  );
}
